import { CommonModule } from '@angular/common';
import { Component, Input, inject, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ClarificationDocument, DocumentResponse } from '@/types';
import { PreviewPdfComponent } from '@/components/shared/preview-pdf/preview-pdf.component';
import { BehaviorSubject, of } from 'rxjs';
import { ClarificationService } from '../../clarification-request.service';

@Component({
  selector: 'sm-output-signed-doc',
  standalone: true,
  imports: [CommonModule, TranslateModule, ButtonModule, PreviewPdfComponent],
  template: `
    <div *ngIf="signedDoc" class="flex flex-col gap-2 p-4 bg-gray-50 border rounded-lg mb-4">
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center gap-3">
          <i class="pi pi-file-pdf text-red-500 text-2xl"></i>
          <div class="flex flex-col">
            <span class="font-semibold text-gray-800">{{ signedDoc.name }}</span>
            <span class="text-xs text-gray-500">{{ signedDoc.fileSize }} • {{ signedDoc.fileExtension | uppercase }}</span>
          </div>
        </div>
      </div>
      
      <div class="border rounded overflow-hidden bg-white shadow-sm relative" style="min-height: 200px;">
        <div *ngIf="loadingPreview" class="absolute inset-0 flex justify-center items-center bg-white bg-opacity-70 z-10">
          <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
        </div>
        <sm-preview-pdf *ngIf="pdfUrl$ | async as url" [src]="getObservable(url)" height="800px"></sm-preview-pdf>
      </div>
    </div>
  `,
})
export class OutputSignedDocComponent implements OnInit {
  @Input() signedDoc?: ClarificationDocument | null;

  clarificationService = inject(ClarificationService);

  loadingPreview = false;
  pdfUrl$ = new BehaviorSubject<string | null>(null);

  ngOnInit() {
    if (this.signedDoc?.alfresco_id) {
      this.loadPreview();
    }
  }

  loadPreview() {
    this.loadingPreview = true;
    this.clarificationService.downloadModelDemande(this.signedDoc!.alfresco_id).subscribe({
      next: (res: DocumentResponse) => {
        const content = res.file || res.fileContent;
        if (!content) {
          this.loadingPreview = false;
          return;
        }
        const byteCharacters = atob(content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: this.signedDoc?.contentType || 'application/pdf' });
        const url = URL.createObjectURL(blob);
        this.pdfUrl$.next(url);
        this.loadingPreview = false;
      },
      error: () => {
        this.loadingPreview = false;
      }
    });
  }

  download() {
    const currentUrl = this.pdfUrl$.getValue();
    if (currentUrl) {
      this.triggerDownload(currentUrl, this.getFileName());
    } else {
      if (!this.signedDoc?.alfresco_id) return;
      this.clarificationService.downloadModelDemande(this.signedDoc.alfresco_id).subscribe((res: DocumentResponse) => {
        const content = res.file || res.fileContent;
        if (!content) return;
        const byteCharacters = atob(content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: this.signedDoc?.contentType || 'application/pdf' });
        const url = URL.createObjectURL(blob);
        this.triggerDownload(url, this.getFileName());
      });
    }
  }

  private triggerDownload(url: string, fileName: string) {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
  }

  private getFileName(): string {
    return (this.signedDoc?.name || 'document') + (this.signedDoc?.fileExtension ? '.' + this.signedDoc?.fileExtension : '');
  }

  getObservable(val: string) {
    return of(val);
  }
}
